/*
  EVDNCE — Wishlist drawer "Coming Soon" label
  ------------------------------------------------------------------
  Swym's wishlist drawer renders inside a SHADOW ROOT, so its Add-to-Cart buttons
  (button.swym-storefront-layout-grid-item-add-to-cart-button) are invisible to a
  normal document.querySelectorAll. Coming-soon products use the
  `coming-soon-product` template and are tagged `swym-disabled`, which makes Swym
  render a greyed / unclickable "Add To Cart" for them. This pierces the shadow DOM,
  finds those buttons, and swaps the greyed button for a clean "Coming Soon" label.

  Detection is by product tag (ground truth), fetched once per handle and cached
  (in-memory + localStorage, so reloads relabel instantly without a refetch).
  Only coming-soon items are ever touched — normal "Add To Cart" and genuine
  "Sold Out" buttons are left exactly as they are.
*/
(function () {
  'use strict';

  var LABEL = 'Coming Soon';
  var COMING_SOON_TAGS = ['swym-disabled', 'coming-soon'];
  var ATC_SELECTOR =
    '.swym-storefront-layout-grid-item-add-to-cart-button, [class*="swym-storefront-layout"][class*="add-to-cart"], .swym-add-to-cart-btn';

  var tagCache = {};
  var LS_KEY = 'evdnce_cs_handles';
  var pending = {};
  var observedRoots = [];
  var scheduled = false;

  function deepCollect(selector) {
    var results = [];
    (function walk(root) {
      var matched;
      try { matched = root.querySelectorAll(selector); } catch (e) { matched = []; }
      for (var i = 0; i < matched.length; i++) results.push(matched[i]);
      var all;
      try { all = root.querySelectorAll('*'); } catch (e) { all = []; }
      for (var j = 0; j < all.length; j++) {
        if (all[j].shadowRoot) walk(all[j].shadowRoot);
      }
    })(document);
    return results;
  }

  function looksLikeAtc(el) {
    if (el.getAttribute('data-evdnce-coming-soon') === '1') return false;
    var t = (el.textContent || '').trim().toLowerCase();
    if (t.indexOf('coming soon') !== -1) return false;
    return t === 'add to cart' || t === 'add to bag';
  }

  function handleFromButton(btn) {
    var node = btn;
    var hops = 0;
    var link = null;
    while (node && hops < 10 && !link) {
      try { link = node.querySelector ? node.querySelector('a[href*="/products/"]') : null; } catch (e) {}
      var parent = node.parentElement;
      if (!parent && node.parentNode && node.parentNode.host) parent = node.parentNode.host;
      node = parent;
      hops++;
    }
    if (!link) return null;
    var m = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function isComingSoonTags(tags) {
    if (!tags) return false;
    var arr = Array.isArray(tags) ? tags : String(tags).split(',');
    for (var i = 0; i < arr.length; i++) {
      var t = String(arr[i]).trim().toLowerCase();
      if (COMING_SOON_TAGS.indexOf(t) !== -1) return true;
    }
    return false;
  }

  function injectStyle() {
    if (document.getElementById('evdnce-coming-soon-style')) return;
    var s = document.createElement('style');
    s.id = 'evdnce-coming-soon-style';
    // Light-DOM (wishlist page) button: hide whatever text Swym renders (Add To Cart,
    // Coming Soon, or a doubled label) and show a single "Coming Soon" via ::before.
    // The label inherits the button's own font sizing so it matches the other buttons.
    s.textContent =
      '.evdnce-coming-soon-btn{position:relative !important;color:transparent !important;' +
      'white-space:nowrap !important;overflow:hidden !important;' +
      'pointer-events:none !important;cursor:default !important;opacity:1 !important;}' +
      '.evdnce-coming-soon-btn::before{content:"Coming Soon";position:absolute;' +
      'left:0;top:0;right:0;bottom:0;display:flex;align-items:center;justify-content:center;' +
      'color:#111 !important;}';
    (document.head || document.documentElement).appendChild(s);
  }

  function relabel(btn) {
    if (btn.getAttribute('data-evdnce-coming-soon') === '1') return;
    btn.setAttribute('data-evdnce-coming-soon', '1');
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('aria-label', LABEL);
    btn.classList.add('evdnce-coming-soon-btn');
    var inShadow = !!(btn.getRootNode && btn.getRootNode().host);
    if (inShadow) {
      // Drawer button lives in a shadow root — external CSS can't reach it, so set the
      // text directly and style inline (Swym does not re-append here).
      btn.textContent = LABEL;
      var st = btn.style;
      st.setProperty('pointer-events', 'none', 'important');
      st.setProperty('cursor', 'default', 'important');
      st.setProperty('opacity', '1', 'important');
      st.setProperty('color', '#111', 'important');
      st.setProperty('background', 'transparent', 'important');
      st.setProperty('border', '1px solid #111', 'important');
    }
    // Light-DOM page button: leave Swym's text untouched; the injected stylesheet
    // overlays the single "Coming Soon" label and hides the underlying text.
  }

  function loadCache() {
    try {
      var raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      var obj = JSON.parse(raw);
      for (var k in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, k)) tagCache[k] = obj[k];
      }
    } catch (e) {}
  }

  function persistCache() {
    try { localStorage.setItem(LS_KEY, JSON.stringify(tagCache)); } catch (e) {}
  }

  function checkHandle(handle, btn) {
    if (Object.prototype.hasOwnProperty.call(tagCache, handle)) {
      if (tagCache[handle]) relabel(btn);
      return;
    }
    if (pending[handle]) return;
    pending[handle] = true;
    fetch('/products/' + handle + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var cs = p ? isComingSoonTags(p.tags) : false;
        tagCache[handle] = cs;
        persistCache();
        delete pending[handle];
        if (cs) relabel(btn);
      })
      .catch(function () { delete pending[handle]; });
  }

  function scan() {
    var btns = deepCollect(ATC_SELECTOR);
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      if (btn.tagName !== 'BUTTON' && btn.tagName !== 'A') continue;
      if (btn.getAttribute('data-evdnce-coming-soon') === '1') {
        // Already handled — re-assert in case Swym re-rendered the button's class/text.
        if (!btn.classList.contains('evdnce-coming-soon-btn')) btn.classList.add('evdnce-coming-soon-btn');
        if (!!(btn.getRootNode && btn.getRootNode().host) && (btn.textContent || '').trim() !== LABEL) {
          btn.textContent = LABEL;
        }
        continue;
      }
      if (!looksLikeAtc(btn)) continue;
      if (btn.disabled || btn.hasAttribute('disabled')) { relabel(btn); continue; }
      var handle = handleFromButton(btn);
      if (handle) checkHandle(handle, btn);
    }
  }

  var mo = new MutationObserver(function () { schedule(); });

  function attachObservers() {
    if (document.body && observedRoots.indexOf(document.body) === -1) {
      observedRoots.push(document.body);
      mo.observe(document.body, { childList: true, subtree: true });
    }
    (function walk(root) {
      var all;
      try { all = root.querySelectorAll('*'); } catch (e) { return; }
      for (var i = 0; i < all.length; i++) {
        var sr = all[i].shadowRoot;
        if (sr && observedRoots.indexOf(sr) === -1) {
          observedRoots.push(sr);
          mo.observe(sr, { childList: true, subtree: true });
          walk(sr);
        }
      }
    })(document);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      attachObservers();
      scan();
    }, 50);
  }

  function init() {
    try { window.__evdnceWishlistDrawerLoaded = 'v5'; } catch (e) {}
    loadCache();
    injectStyle();
    attachObservers();
    scan();

    // Opening the drawer is a click, and its shadow root mounts lazily with no
    // observable mutation. So on ANY click: (1) block add-to-cart on a coming-soon
    // drawer button as a hard safety net, and (2) rescan rapidly to relabel the
    // freshly opened drawer within a frame or two instead of waiting on the poll.
    document.addEventListener('click', function (e) {
      try {
        var path = e.composedPath ? e.composedPath() : [];
        for (var i = 0; i < path.length; i++) {
          var el = path[i];
          if (!el || el.nodeType !== 1 || !el.matches) continue;
          if (el.matches(ATC_SELECTOR)) {
            var handle = handleFromButton(el);
            var known = handle && tagCache[handle];
            if (
              el.getAttribute('data-evdnce-coming-soon') === '1' ||
              el.disabled || el.hasAttribute('disabled') || known
            ) {
              e.preventDefault();
              e.stopImmediatePropagation();
              relabel(el);
            }
            break;
          }
        }
      } catch (err) {}
      scan();
      setTimeout(function () { attachObservers(); scan(); }, 40);
      setTimeout(function () { attachObservers(); scan(); }, 140);
      setTimeout(function () { attachObservers(); scan(); }, 320);
    }, true);

    setInterval(function () { attachObservers(); scan(); }, 400);
    setTimeout(function () { attachObservers(); scan(); }, 600);
    setTimeout(function () { attachObservers(); scan(); }, 1800);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
